import React, { useEffect } from 'react'
import { Button, Heading, Icon, Link, Money, Skeleton, Text } from '@afterpaytouch/core'
import { Head } from '../../component'
import { Route } from '../../config/router'
import { TrackPageViewed, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { CheckoutPage, TrackingEvent } from '../../model'
import { setWelcomeWithInitialLimitSeen, useAppDispatch, useAppRouter, useGetCreditLimitQuery } from '../../state'
import { useUkRegFlags } from '../../state/ukRegulation/hooks'
import { useCheckoutSizes } from '../../utils/responsive'
import { Trans, useTranslation } from '../../utils/convergence'
import styles from './styles.module.css'

export const WelcomeWithInitialLimitPage: CheckoutPage = () => {
  const router = useAppRouter()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { headingSize, textSize } = useCheckoutSizes()
  const { data: creditLimitData, isLoading, isError } = useGetCreditLimitQuery()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { isUkCreditCheckEnabled, isUkRegulationEnabled } = useUkRegFlags()

  const limitEventProperties = {
    isCreditChecked: isUkCreditCheckEnabled,
    isAffordabilityChecked: isUkRegulationEnabled,
    isAffordabilityAnswered: isUkRegulationEnabled,
  }

  const handleContinue = (): void => {
    logEvent(TrackingEvent.PRESSED_WELCOME_WITH_INITIAL_LIMIT_CONTINUE_BUTTON, limitEventProperties)
    dispatch(setWelcomeWithInitialLimitSeen())
    router.replace(Route.LOGGED_IN)
  }

  useEffect(() => {
    if (isError) {
      dispatch(setWelcomeWithInitialLimitSeen())
      router.replace(Route.LOGGED_IN)
    }
  }, [isError, dispatch, router])

  if (isError) {
    return null
  }

  return (
    <div className={styles.container}>
      <Head title={t('welcomeWithInitialLimit:heading')} />
      <div className={styles.icon}>
        <span className={styles.successIcon} data-testid='welcome-check-icon'>
          <Icon name='Check' color='Inverse' size='Medium' />
        </span>
      </div>
      <div className={styles.heading}>
        <Heading size={headingSize}>{t('welcomeWithInitialLimit:heading')}</Heading>
      </div>
      <div className={styles.subtitle}>
        <Text size={textSize} color='Subtle'>
          {t('welcomeWithInitialLimit:estimatedSpend')}
        </Text>
      </div>
      <div className={styles.amount}>
        <Heading renderAs='span' size={headingSize}>
          <span className={isLoading ? styles.visuallyInvisible : ''}>
            {creditLimitData?.creditLimit != null &&
              Money({
                amount: creditLimitData.creditLimit.amount,
                currency: creditLimitData.creditLimit.currency,
                locale: router.locale,
              })}
          </span>
          {isLoading && <Skeleton />}
        </Heading>
      </div>
      <div className={styles.sections}>
        <div className={styles.sectionTitle}>
          <Text size={textSize} bold>
            {t('welcomeWithInitialLimit:uniqueAmountTitle')}
          </Text>
        </div>
        <div className={styles.sectionBody}>
          <Text size={textSize} color='Subtle'>
            <Trans
              i18nKey='welcomeWithInitialLimit:uniqueAmountBody'
              components={{
                clickHereLink: <Link kind='Underline' href={t('common:spendLimitInfoUrl')} target='_blank' />,
              }}
            />
          </Text>
        </div>
        <div className={styles.sectionTitle}>
          <Text size={textSize} bold>
            {t('welcomeWithInitialLimit:justSoYouKnowTitle')}
          </Text>
        </div>
        <div className={styles.sectionBody}>
          <Text size={textSize} color='Subtle'>
            {t('welcomeWithInitialLimit:justSoYouKnowBody')}
          </Text>
        </div>
      </div>
      <div className={styles.cta}>
        <Button.Primary size='Large' padding='Fluid' onClick={handleContinue} testNameSpace='welcome-continue'>
          {t('welcomeWithInitialLimit:continue')}
        </Button.Primary>
      </div>
      <TrackPageViewed eventProperties={limitEventProperties} />
    </div>
  )
}
